import type { CoachingSession } from '@efdigital/ll-addon-data';
import { CoachingSessionsTable } from '@efdigital/ll-admin-v2/components/coaching/sessions/coaching-sessions-table.gts';
import { PaginationByOffset } from '@efdigital/ll-admin-v2/components/pagination.gts';
import { getPaginationTotal } from '@efdigital/ll-admin-v2/helpers/get-pagination-total';
import type { TOC } from '@ember/component/template-only';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';
import type { RequestInfo } from '@warp-drive/core/types/request';
import { Request } from '@warp-drive/ember';

interface SessionsListSignature {
  Args: {
    /** Built by `queryCoachingSessions` — see the coaching-sessions builder. */
    query: RequestInfo<ReactiveDataDocument<CoachingSession[]>>;
    offset: number;
    limit: number;
    onOffsetChange: (offset: number) => void;
  };
  Element: HTMLDivElement;
}

/**
 * Renders one page of sessions with its pager, for any scope. Presentational
 * only: the caller owns the query and the pagination window, so this is shared
 * by the unfiltered admin list and every in-context list without either of them
 * re-implementing loading or paging behaviour.
 *
 * The empty state belongs to `CoachingSessionsTable`, which already renders one
 * — this must not guard the table on `data.length` or that styled message is
 * replaced by nothing.
 */
export const SessionsList: TOC<SessionsListSignature> = <template>
  <div ...attributes>
    <Request @query={{@query}}>
      <:content as |content|>
        <PaginationByOffset
          @offset={{@offset}}
          @limit={{@limit}}
          @total={{getPaginationTotal content}}
          @onOffsetChange={{@onOffsetChange}}
          class="d-flex flex-row justify-content-center my-4"
        />
        <CoachingSessionsTable @sessions={{content.data}} />
      </:content>
      <:loading>
        <div class="text-center py-5">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      </:loading>
      <:error>
        <div class="alert alert-danger" role="alert">Couldn’t load sessions.</div>
      </:error>
    </Request>
  </div>
</template>;
